import type { BrandKit } from '@/types'
import type {
    Background,
    Pattern,
    ThemeId,
    ThemeStyle
} from '@/lib/design/types'

export type Theme = ThemeStyle

const NO_PATTERN: Pattern = { type: 'none', color: '#000000', opacity: 0 }

export const THEMES: Array<Theme> = [
    {
        id: 'ink',
        name: 'Ink',
        description: 'Heavy type on near black with a coral highlight.',
        palette: { background: '#121110', surface: '#1F1C19', text: '#F5EFE6', muted: '#B9AFA3', accent: '#FF6B4A', accentText: '#121110' },
        fonts: { heading: 'archivo-black', body: 'inter' },
        headingWeight: 400,
        bodyWeight: 400,
        headingUppercase: false,
        headingTracking: -1,
        headingLineHeight: 1.08,
        headingScale: 1,
        highlightStyle: 'color',
        background: 'solid',
        photoCover: false,
        pattern: NO_PATTERN,
        frame: 'border',
        align: 'left',
        coverPlacement: 'center'
    },
    {
        id: 'paper',
        name: 'Paper',
        description: 'Editorial serif on warm paper, marker highlights.',
        palette: { background: '#F4EEE3', surface: '#FFFFFF', text: '#1E1B16', muted: '#6B6158', accent: '#F4B183', accentText: '#1E1B16' },
        fonts: { heading: 'dm-serif', body: 'inter' },
        headingWeight: 400,
        bodyWeight: 400,
        headingUppercase: false,
        headingTracking: -1,
        headingLineHeight: 1.06,
        headingScale: 1.08,
        highlightStyle: 'marker',
        background: 'solid',
        photoCover: false,
        pattern: NO_PATTERN,
        frame: 'none',
        align: 'left',
        coverPlacement: 'center'
    },
    {
        id: 'citrus',
        name: 'Citrus',
        description: 'Loud yellow with condensed capitals.',
        palette: { background: '#FFD43B', surface: '#FFFFFF', text: '#111111', muted: '#3D3A2F', accent: '#111111', accentText: '#FFD43B' },
        fonts: { heading: 'anton', body: 'space-grotesk' },
        headingWeight: 400,
        bodyWeight: 500,
        headingUppercase: true,
        headingTracking: 0,
        headingLineHeight: 1.02,
        headingScale: 1.22,
        highlightStyle: 'marker',
        background: 'solid',
        photoCover: false,
        pattern: NO_PATTERN,
        frame: 'none',
        align: 'left',
        coverPlacement: 'center'
    },
    {
        id: 'gradient-pop',
        name: 'Gradient Pop',
        description: 'Violet to pink gradient with bright yellow accents.',
        palette: { background: '#6D28D9', surface: '#FFFFFF', text: '#FFFFFF', muted: '#F3E8FF', accent: '#FDE047', accentText: '#3B0764' },
        fonts: { heading: 'bricolage', body: 'figtree' },
        headingWeight: 800,
        bodyWeight: 500,
        headingUppercase: false,
        headingTracking: -1.5,
        headingLineHeight: 1.04,
        headingScale: 1.04,
        highlightStyle: 'color',
        background: 'gradient',
        gradient: { to: '#EC4899', angle: 135 },
        photoCover: false,
        pattern: NO_PATTERN,
        frame: 'none',
        align: 'center',
        coverPlacement: 'center'
    },
    {
        id: 'photo-overlay',
        name: 'Photo Overlay',
        description: 'The article photo on the cover, deep navy slides after.',
        palette: { background: '#0F172A', surface: '#1E293B', text: '#FFFFFF', muted: '#CBD5E1', accent: '#38BDF8', accentText: '#0F172A' },
        fonts: { heading: 'montserrat', body: 'inter' },
        headingWeight: 800,
        bodyWeight: 400,
        headingUppercase: false,
        headingTracking: -1,
        headingLineHeight: 1.06,
        headingScale: 0.98,
        highlightStyle: 'color',
        background: 'solid',
        photoCover: true,
        pattern: NO_PATTERN,
        frame: 'none',
        align: 'left',
        coverPlacement: 'bottom'
    },
    {
        id: 'text-card',
        name: 'Text Card',
        description: 'Looks like a text post with your avatar and handle.',
        palette: { background: '#E9EEF3', surface: '#FFFFFF', text: '#0F1419', muted: '#536471', accent: '#1D9BF0', accentText: '#FFFFFF' },
        fonts: { heading: 'inter', body: 'inter' },
        headingWeight: 700,
        bodyWeight: 400,
        headingUppercase: false,
        headingTracking: -0.5,
        headingLineHeight: 1.2,
        headingScale: 0.78,
        highlightStyle: 'color',
        background: 'solid',
        photoCover: false,
        pattern: NO_PATTERN,
        frame: 'card',
        align: 'left',
        coverPlacement: 'center'
    },
    {
        id: 'notebook',
        name: 'Notebook',
        description: 'Lined paper, handwritten headings, yellow marker.',
        palette: { background: '#FFFDF6', surface: '#FFFFFF', text: '#1F2937', muted: '#4B5563', accent: '#FEF08A', accentText: '#1F2937' },
        fonts: { heading: 'caveat', body: 'figtree' },
        headingWeight: 700,
        bodyWeight: 500,
        headingUppercase: false,
        headingTracking: 0,
        headingLineHeight: 1.05,
        headingScale: 1.3,
        highlightStyle: 'marker',
        background: 'solid',
        photoCover: false,
        pattern: { type: 'notebook', color: '#9CC3E6', opacity: 0.7 },
        frame: 'margin',
        align: 'left',
        coverPlacement: 'center'
    },
    {
        id: 'blueprint',
        name: 'Blueprint',
        description: 'Engineering grid on cobalt with amber accents.',
        palette: { background: '#1E3A8A', surface: '#1E40AF', text: '#F8FAFC', muted: '#BFDBFE', accent: '#FBBF24', accentText: '#1E3A8A' },
        fonts: { heading: 'space-grotesk', body: 'jetbrains' },
        headingWeight: 700,
        bodyWeight: 400,
        headingUppercase: false,
        headingTracking: -1.5,
        headingLineHeight: 1.06,
        headingScale: 1,
        highlightStyle: 'color',
        background: 'solid',
        photoCover: false,
        pattern: { type: 'grid', color: '#93C5FD', opacity: 0.16 },
        frame: 'border',
        align: 'left',
        coverPlacement: 'center'
    }
]

const THEME_MAP = new Map(THEMES.map(theme => [theme.id, theme]))

export const isThemeId = (value: unknown): value is ThemeId => typeof value === 'string' && THEME_MAP.has(value as ThemeId)

export const getTheme = (id: string): Theme => THEME_MAP.get(id as ThemeId) ?? THEMES[0]

const hexToRgb = (hex: string): [number, number, number] | null => {
    const clean = hex.replace('#', '')
    const full = clean.length === 3 ? clean.split('').map(char => char + char).join('') : clean

    if (!/^[0-9a-f]{6}$/i.test(full)) return null

    return [parseInt(full.slice(0, 2), 16), parseInt(full.slice(2, 4), 16), parseInt(full.slice(4, 6), 16)]
}

const luminance = (hex: string): number => {
    const rgb = hexToRgb(hex)

    if (!rgb) return 0.5

    const [r, g, b] = rgb.map(value => {
        const channel = value / 255

        return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
    })

    return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

export const mixColors = (from: string, to: string, amount: number): string => {
    const a = hexToRgb(from)
    const b = hexToRgb(to)

    if (!a || !b) return from

    const mixed = a.map((value, index) => Math.round(value + (b[index] - value) * amount))

    return `#${mixed.map(value => value.toString(16).padStart(2, '0')).join('')}`
}

export const readableOn = (background: string): string => luminance(background) > 0.45 ? '#111111' : '#FFFFFF'

// A theme restyled with the colors and faces of the person's brand kit.
export const themeWithBrand = (theme: Theme, brand: BrandKit | null): Theme => {
    if (!brand) return theme

    const { background, text, accent, secondary } = brand.colors
    const markerText = readableOn(accent)

    return {
        ...theme,
        palette: {
            background,
            surface: theme.frame === 'card' ? mixColors(background, '#FFFFFF', 0.85) : secondary,
            text: theme.frame === 'card' ? readableOn(mixColors(background, '#FFFFFF', 0.85)) : text,
            muted: mixColors(text, background, 0.3),
            accent,
            accentText: markerText
        },
        fonts: {
            heading: brand.headingFont || theme.fonts.heading,
            body: brand.bodyFont || theme.fonts.body
        },
        gradient: theme.gradient ? { ...theme.gradient, to: secondary } : undefined,
        pattern: theme.pattern.type === 'none' ? theme.pattern : { ...theme.pattern, color: mixColors(text, background, 0.5) }
    }
}

export const themeBackground = (theme: Theme): Background => {
    if (theme.background === 'gradient' && theme.gradient) {
        return { type: 'gradient', from: theme.palette.background, to: theme.gradient.to, angle: theme.gradient.angle }
    }

    return { type: 'solid', color: theme.palette.background }
}
